

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Handle, Position } from '@xyflow/react';

import type { EventNodeData } from '../types';

interface EventNodeProps {
  data: EventNodeData;
  selected?: boolean;
}

const STATUS_COLORS_EVENT = {
  complete: { bg: '#10b98115', border: '#10b981', text: '#059669' },
  partial: { bg: '#f59e0b15', border: '#f59e0b', text: '#d97706' },
  notWritten: { bg: '#6b728015', border: '#6b7280', text: '#4b5563' },
};

/**
 * Event node with status indicator and date display
 */
export function EventNode({ data, selected }: EventNodeProps) {
  const statusConfig = STATUS_COLORS_EVENT[data.status as keyof typeof STATUS_COLORS_EVENT] || STATUS_COLORS_EVENT.notWritten;

  return (
    <>
      <Handle type="source" position={Position.Top} id="floating" style={{ opacity: 0, top: '50%', left: '50%' }} />
      <Handle type="target" position={Position.Top} id="floating" style={{ opacity: 0, top: '50%', left: '50%' }} />
      <Paper
      elevation={selected ? 6 : 1}
      sx={{
        p: 1.5,
        minWidth: 170,
        maxWidth: 220,
        bgcolor: 'background.paper',
        border: 1.5,
        borderColor: selected ? (data.color || 'primary.main') : (data.color || 'divider'),
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: statusConfig.border,
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <Box sx={{ pl: 1 }}>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          noWrap
          sx={{ mb: 0.25 }}
        >
          {data.event.name}
        </Typography>
        {data.event.date && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            📅 {data.event.date}
          </Typography>
        )}
        <Chip
          size="small"
          label={data.status === 'complete' ? '✓ Complete' : data.status === 'partial' ? '◐ Partial' : '○ Not Written'}
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 500,
            bgcolor: statusConfig.bg,
            color: statusConfig.text,
            border: `1px solid ${statusConfig.border}40`,
          }}
        />
      </Box>
    </Paper>
    </>
  );
}

export default EventNode;
